import React, { useEffect, useState } from 'react'
import { bookmarkApi } from '../api/bookmark'
import { toastUtil } from '../utils/toast'
import {
  setPlaceSaved,
  usePlaceBookmarkStore,
} from '../stores/placeBookmarkStore'
import PlaceSaveSheet from './PlaceSaveSheet'
import { takeAfterCreate } from '../utils/platelistCreateReturn'

interface BookmarkButtonProps {
  storeId: string
  buttonSize?: number
  isBookmarked: boolean // 북마크 상태 (필수)
  /** 표시용 장소명 — 시트 상단에 보여준다 */
  placeName?: string
  /** 표시용 주소 — 시트 상단 장소명 아래 */
  placeAddress?: string
  /**
   * 저장 시트의 열림/닫힘. 지도처럼 이미 바텀시트가 떠 있는 화면은 이걸 받아
   * 자기 시트를 잠시 숨긴다(닫는 게 아니라 숨기는 것 — 저장 시트를 닫으면
   * 원래 시트가 그대로 다시 보여야 한다).
   */
  onSheetOpenChange?: (isOpen: boolean) => void
}

/**
 * 장소 저장 버튼.
 *
 * fill = "내가 저장한 장소인가". 탭하면 항상 저장 시트가 열리고, 어느
 * 플레이트리스트에 넣고 뺄지는 시트에서 정한다. 저장 전이면 누르는 즉시
 * 저장(리스트 미지정)하고 시트를 연다.
 */
const BookmarkButton: React.FC<BookmarkButtonProps> = ({
  storeId,
  buttonSize = 26,
  isBookmarked: initialIsBookmarked,
  placeName,
  placeAddress,
  onSheetOpenChange,
}) => {
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  /** 시트를 연 뒤 뒤늦게 저장이 끝나면 올려서 시트 목록을 다시 읽게 한다 */
  const [reloadToken, setReloadToken] = useState(0)

  const setSheetOpen = (open: boolean) => {
    setIsSheetOpen(open)
    onSheetOpenChange?.(open)
  }

  const placeId = parseInt(storeId, 10)

  /*
   * 리스트를 새로 만들고 돌아왔는가.
   *
   * 시트에서 "추가"를 누르면 전체 페이지로 넘어가고, 그 사이
   * 이 화면은 통째로 내려갔다 돌아온다. 떠나기 전에 적어둔 쪽지가 내 것이면
   * 시트를 다시 열고, 방금 만든 리스트에 이 장소를 담는다 — 저장하려다 만든
   * 리스트이므로 거기 담는 것이 하려던 일이다.
   */
  useEffect(() => {
    if (Number.isNaN(placeId)) return
    const back = takeAfterCreate(`place-save:${placeId}`)
    if (!back) return

    setSheetOpen(true)
    if (!back.createdId) return

    bookmarkApi
      .addPlacesToCategory(back.createdId, [placeId])
      .then(() => {
        setIsBookmarked(true)
        setReloadToken((n) => n + 1)
      })
      .catch(() => toastUtil.error('새 리스트에 담지 못했어요'))
    // placeId 가 바뀌면 다른 장소의 버튼이다 — 그때만 다시 본다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId])

  // 사용자가 이 세션에서 저장/해제한 적이 있으면 그 값이 목록 응답보다 최신이다.
  // 같은 장소가 화면에 여러 번 떠 있어도 아이콘이 함께 움직인다.
  const overridden = usePlaceBookmarkStore((state) =>
    Number.isNaN(placeId) ? undefined : state.saved[String(placeId)]
  )
  const isBookmarked = overridden ?? initialIsBookmarked
  const setIsBookmarked = (saved: boolean) => setPlaceSaved(placeId, saved)

  const handleClick = async () => {
    if (Number.isNaN(placeId)) {
      toastUtil.error('장소 정보를 불러오는데 실패했습니다')
      return
    }

    // 시트를 먼저 연다. 저장 요청을 기다렸다 열면 네트워크가 느릴 때
    // 탭이 씹힌 것처럼 보인다(수백 ms 동안 아무 반응 없음).
    setSheetOpen(true)

    if (!isBookmarked) {
      setIsBookmarked(true)
      try {
        await bookmarkApi.toggleBookmark(placeId, {})
        setReloadToken((n) => n + 1)
        // 여기선 알리지 않는다. 바로 저장 시트가 열려 어느 리스트에 넣을지
        // 고르게 되므로, 리스트를 고르는 순간에만 알린다(PlaceSaveSheet).
      } catch (error: any) {
        setIsBookmarked(false)
        toastUtil.error(error.response?.data?.message || '저장에 실패했습니다')
      }
    }
  }

  return (
    <>
      <button
        className="flex items-center justify-center"
        style={{ width: buttonSize, height: buttonSize }}
        onClick={handleClick}
      >
        <img
          src={
            isBookmarked
              ? '/images/bookmark-filled.png'
              : '/images/bookmark-empty.png'
          }
          alt="북마크"
          className="w-full h-full"
        />
      </button>

      {isSheetOpen && (
        <PlaceSaveSheet
          isOpen
          onClose={() => setSheetOpen(false)}
          placeId={placeId}
          placeName={placeName}
          placeAddress={placeAddress}
          onSavedChange={setIsBookmarked}
          reloadToken={reloadToken}
        />
      )}
    </>
  )
}

export default BookmarkButton
